import L from "leaflet";
import { arcControlPoint } from "./routeArc";
import type { LatLngPoint } from "./smoothing";

/**
 * 懸空弧線的算繪層：一條二次貝茲路徑，不是折線。
 *
 * 為什麼不用 L.Polyline：Leaflet 的 latLngToLayerPoint 會把投影後的座標 _round() 成整數像素。
 * 折線有幾十個頂點，曲線一平移，每個頂點各自在不同時間跳 1 個像素——整條線就在那裡抖。
 * 節流、換 canvas、關掉 smoothFactor 都治不了這層，因為取整數發生在更下面。
 *
 * 改成貝茲之後只需要投影三個點（起點、控制點、終點），而且我們自己用 map.project() 算、
 * 不經過會取整數的那支 API，小數點精度完整保留。瀏覽器再把 Q 指令算成平滑曲線——
 * 曲線本身不再由頂點拼出來，就沒有頂點可以抖。順帶一提，每幀要寫的也從幾十個座標
 * 變成六個數字。
 *
 * 繼承 L.Path 而不是自己掛 DOM：pane、縮放動畫、viewreset、樣式套用這些 Leaflet 都處理好了，
 * 我們只要負責「這條路徑長什麼樣」。
 */

export interface BezierArcLayer extends L.Path {
  /** 換一組端點並立刻重畫（每幀呼叫） */
  setPoints(from: LatLngPoint, to: LatLngPoint): void;
}

/** L.Path.extend 沒有型別定義可用，轉型集中在這裡，不外流。 */
const PathBase = L.Path as unknown as {
  extend(props: Record<string, unknown>): new (...args: unknown[]) => BezierArcLayer;
};

const BezierArc = PathBase.extend({
  initialize(this: any, from: LatLngPoint, to: LatLngPoint, curvature: number, options: L.PathOptions) {
    L.Util.setOptions(this, options);
    this._curvature = curvature;
    this._setGeometry(from, to);
  },

  _setGeometry(this: any, from: LatLngPoint, to: LatLngPoint) {
    this._from = from;
    this._to = to;
    this._ctrl = arcControlPoint(from, to, this._curvature);
  },

  setPoints(this: any, from: LatLngPoint, to: LatLngPoint) {
    this._setGeometry(from, to);
    // 縮放動畫期間地圖的 pane 正被 CSS transform 拉伸，此時投影出來的座標對不上畫面。
    // 動畫結束 Leaflet 會呼叫 _reset 重新投影，所以這裡直接跳過就好。
    if (!this._map || this._map._animatingZoom) return;
    this._project();
    this._update();
  },

  _project(this: any) {
    const map = this._map;
    const zoom = map.getZoom();
    const origin = map.getPixelOrigin();
    // 不用 latLngToLayerPoint：那支會 _round()，而取整數正是抖動的來源。
    // project() 回傳的是小數，減掉 pixelOrigin（每次 viewreset 才變一次）不影響平滑度。
    const toPoint = (p: LatLngPoint) => map.project(L.latLng(p.lat, p.lng), zoom).subtract(origin);
    this._a = toPoint(this._from);
    this._c = toPoint(this._ctrl);
    this._b = toPoint(this._to);
  },

  _update(this: any) {
    if (!this._map || !this._a) return;
    // 保留兩位小數：肉眼分不出更細的差別，但字串短一點
    const f = (n: number) => n.toFixed(2);
    this._renderer._setPath(
      this,
      `M${f(this._a.x)} ${f(this._a.y)} Q${f(this._c.x)} ${f(this._c.y)} ${f(this._b.x)} ${f(this._b.y)}`,
    );
  },

  /** Path 在某些路徑上會問邊界；用兩個端點即可（弧頂的凸出由 fitBounds 那側自己算）。 */
  getBounds(this: any) {
    return L.latLngBounds([
      [this._from.lat, this._from.lng],
      [this._to.lat, this._to.lng],
    ]);
  },
});

export const createBezierArc = (
  from: LatLngPoint,
  to: LatLngPoint,
  curvature: number,
  options: L.PathOptions,
): BezierArcLayer => new BezierArc(from, to, curvature, options);
